import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAllSubbrands,
  fetchAllDrinksCategories,
  fetchSubbrandVariants as fetchSubbrandVariantsApi,
} from "../api/marketOverviewApi";
import { getValidCountryCodes, normalizeCountryCode } from "../utils/Homepage/homepageUtils.jsx";

const initialState = {
  appliedFilters: [],
  appliedSorters: [],
  currentCountry: null,
  // One-shot signal from the refresh-notification email deep-link: the country
  // the email is about, so Market Overview can seed its Region/Market selection
  // to that market on arrival. Consumed and cleared by the Market Overview
  // FilterSelectors; never persisted (see filtersPersistConfig blacklist).
  deepLinkCountry: null,
  // filterTextOptions only retains fields not covered by filterDimensionsSlice
  // (brand trees and drinks categories use separate endpoints)
  filterTextOptions: {
    listing_brand_category: [],
    not_listing_brand_category: [],
    is_offering_brands_tree: [],
    not_offering_brands_tree: [],
  },
  // The workspace and market the brand trees belong to, so variants that
  // arrive after a switch are dropped.
  brandTreeScope: null,
  // Load state of each subbrand node's variants, keyed by
  // subbrandVariantsKey(): "loading" | "loaded" | "error".
  subbrandVariants: {},
  error: null,
  loading: false,
};

const BRAND_TREES = ["is_offering_brands_tree", "not_offering_brands_tree"];

// A subbrand node is one subbrand name under one company and brand. The SKU
// filter matches on that name, so its variants are loaded and kept per name,
// never per subbrand id: one id can carry two names, and one name two ids.
export const subbrandVariantsKey = ({ company, brand, subbrand }) =>
  JSON.stringify([company, brand, subbrand]);

// The display text of a variant node's value: its subbrand, volume and
// packaging. Returns null for any other tree value, which is not a JSON object.
export const variantKeyLabel = (value) => {
  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null; // a company, brand or subbrand key
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const { subbrand, volume, packaging_type, packaging_size } = parsed;
  return [subbrand, volume, packaging_type, packaging_size].filter(Boolean).join(" ") || null;
};

// Calls fn on the subbrand node with these names, in both brand trees.
const forEachSubbrandNode = (state, { company, brand, subbrand }, fn) => {
  BRAND_TREES.forEach((tree) => {
    (state.filterTextOptions[tree] || [])
      .filter((companyNode) => companyNode.label === company)
      .forEach((companyNode) =>
        (companyNode.children || [])
          .filter((brandNode) => brandNode.label === brand)
          .forEach((brandNode) =>
            (brandNode.children || [])
              .filter((subbrandNode) => subbrandNode.label === subbrand)
              .forEach(fn)
          )
      );
  });
};

const isCurrentScope = (state, arg) =>
  state.brandTreeScope?.id_client === arg.id_client &&
  state.brandTreeScope?.country === arg.country;

export const fetchAllSubbrandsTree = createAsyncThunk(
  "filters/fetchAllSubbrandsTree",
  async ({ id_client, country }, { rejectWithValue }) => {
    try {
      const res = await fetchAllSubbrands({ id_client, country });
      return res;
    } catch (error) {
      return rejectWithValue(error.message || "fail fetching all subbrands tree");
    }
  }
);

// Loads one subbrand node's variants when the user opens it in the brand
// tree. A node already loading or loaded is skipped; one that failed retries.
export const fetchSubbrandVariants = createAsyncThunk(
  "filters/fetchSubbrandVariants",
  async ({ id_client, country, company, brand, subbrand }, { rejectWithValue }) => {
    try {
      return await fetchSubbrandVariantsApi({ id_client, country, company, brand, subbrand });
    } catch (error) {
      return rejectWithValue(error.message || "fail fetching subbrand variants");
    }
  },
  {
    condition: (arg, { getState }) => {
      const status = getState().filters.subbrandVariants?.[subbrandVariantsKey(arg)];
      return status !== "loading" && status !== "loaded";
    },
  }
);

export const fetchAllDrinksCategoriesList = createAsyncThunk(
  "filters/fetchAllDrinksCategoriesList",
  async ({ id_client, country }, { rejectWithValue }) => {
    try {
      const res = await fetchAllDrinksCategories({ id_client, country });
      return res;
    } catch (error) {
      return rejectWithValue(error.message || "fail fetching all drinks categories");
    }
  }
);

const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    setColumnFilter: (state, action) => {
      state.appliedFilters = action.payload;
    },
    setColumnSorter: (state, action) => {
      state.appliedSorters = action.payload;
    },
    removeFilterByKey: (state, action) => {
      const key = action.payload;

      if (state.appliedFilters[key] !== undefined) {
        state.appliedFilters[key] = null;
      }
    },
    setCurrentCountry: (state, action) => {
      if (state.currentCountry === action.payload) return;
      state.currentCountry = action.payload;
    },
    setDeepLinkCountry: (state, action) => {
      state.deepLinkCountry = action.payload;
    },
    clearAllFilters: (state) => {
      state.appliedFilters = [];
    },
    setFilterTextOptions: (state, action) => {
      state.filterTextOptions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSubbrandsTree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllSubbrandsTree.fulfilled, (state, action) => {
        state.loading = false;
        const mapSubbrandsToTree = (payload) => {
          const companies = payload?.companies || [];
          return companies.map((company) => ({
            label: company.company,
            value: `company_${company.company}`,
            children: (company.brands || []).map((brand) => ({
              label: brand.brand,
              value: `brand_${brand.brand}`,
              // A subbrand's variants load when it opens, through
              // fetchSubbrandVariants. Until then it has no children.
              children: (brand.subbrands || []).map((sub) => ({
                label: sub.subbrand,
                value: `subbrand_${sub.subbrand}`,
                isLeaf: !sub.has_variants,
                ...(sub.has_variants
                  ? {
                      variantsFor: {
                        company: company.company,
                        brand: brand.brand,
                        subbrand: sub.subbrand,
                      },
                    }
                  : {}),
              })),
            })),
          }));
        };

        const treeData = mapSubbrandsToTree(action.payload);
        state.filterTextOptions.is_offering_brands_tree = treeData;
        state.filterTextOptions.not_offering_brands_tree = treeData;
        const { id_client, country } = action.meta?.arg || {};
        state.brandTreeScope = { id_client, country };
        state.subbrandVariants = {};
      })
      .addCase(fetchAllSubbrandsTree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchSubbrandVariants.pending, (state, action) => {
        const arg = action.meta.arg;
        if (!isCurrentScope(state, arg)) return;
        const key = subbrandVariantsKey(arg);
        state.subbrandVariants[key] = "loading";
        forEachSubbrandNode(state, arg, (node) => {
          node.children = [
            {
              label: "Loading variants…",
              value: `__variants_loading__${key}`,
              disabled: true,
              isLeaf: true,
            },
          ];
        });
      })
      .addCase(fetchSubbrandVariants.fulfilled, (state, action) => {
        const arg = action.meta.arg;
        if (!isCurrentScope(state, arg)) return;
        const { subbrand } = arg;
        state.subbrandVariants[subbrandVariantsKey(arg)] = "loaded";
        const skus = action.payload?.skus || [];
        forEachSubbrandNode(state, arg, (node) => {
          if (skus.length === 0) {
            delete node.children;
            node.isLeaf = true;
            return;
          }
          // The value is a JSON-stringified SKUIdentifier so that
          // splitBrandsAndSkus() in useHomePage.js routes it through the
          // `skus[]` bucket and onto brand_filter.brands.include.skus,
          // matching the backend SKUIdentifier(subbrand, packaging_type,
          // packaging_size, volume) contract in execution_view_models.py.
          node.children = skus.map((sku) => ({
            label: sku.label,
            value: JSON.stringify({
              subbrand,
              packaging_type: sku.packaging_type ?? null,
              packaging_size: sku.packaging_size ?? null,
              volume: sku.volume ?? null,
            }),
          }));
        });
      })
      .addCase(fetchSubbrandVariants.rejected, (state, action) => {
        const { company, brand, subbrand } = action.meta.arg;
        if (!isCurrentScope(state, action.meta.arg)) return;
        const key = subbrandVariantsKey(action.meta.arg);
        state.subbrandVariants[key] = "error";
        forEachSubbrandNode(state, action.meta.arg, (node) => {
          node.children = [
            {
              label: "Could not load variants.",
              value: `__variants_error__${key}`,
              disabled: true,
              isLeaf: true,
              retryVariantsFor: { company, brand, subbrand },
            },
          ];
        });
      });

    builder
      .addCase(fetchAllDrinksCategoriesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDrinksCategoriesList.fulfilled, (state, action) => {
        state.loading = false;
        const options = (action.payload || []).map((item) => ({
          label: item,
          value: item,
        }));
        state.filterTextOptions.listing_brand_category = options;
        state.filterTextOptions.not_listing_brand_category = options;
      })
      .addCase(fetchAllDrinksCategoriesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Clear currentCountry atomically with the workspace change on the
    // thunk path. authSlice's switchWorkspaceAndUpdateCountry.pending sets the
    // new id_client (+ status "switching") in this same dispatch, so pairing the
    // country clear here makes the (new id_client, old country) intermediate
    // unrepresentable — not merely cleared a tick later by the thunk body's
    // dispatch(setCurrentCountry(null)) (kept as belt-and-suspenders). Matched by
    // action-type string to avoid a circular import (same rationale as above).
    // Column filters and sorters name the old workspace's columns and values,
    // and a switch can start from a page that does not show the table, so they
    // clear here too rather than in the table's own switch handling.
    builder.addCase("auth/switchWorkspaceAndUpdateCountry/pending", (state) => {
      state.currentCountry = null;
      state.appliedFilters = [];
      state.appliedSorters = [];
    });

    // The one place that picks the selected country when a country list
    // arrives: a requested target the list contains, else the selected country
    // if the list still contains it, else the API's default_country. Explicit
    // user picks dispatch setCurrentCountry directly.
    builder.addCase("auth/fetchClientCountries/fulfilled", (state, action) => {
      const valid = getValidCountryCodes(action.payload?.regions);
      const target = normalizeCountryCode(action.meta?.arg?.targetCountry);
      if (target && valid.includes(target)) {
        state.currentCountry = target;
      } else if (!valid.includes(state.currentCountry)) {
        state.currentCountry = action.payload?.default_country ?? null;
      }
    });
  },
});

export const {
  setColumnFilter,
  removeFilterByKey,
  clearAllFilters,
  setColumnSorter,
  setCurrentCountry,
  setDeepLinkCountry,
} = filtersSlice.actions;
export const selectAppliedFilters = (state) => state.filters.appliedFilters;
export default filtersSlice.reducer;
